import { useEffect, useState } from "react";
import { BellPlus, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const todayStr = () => new Date().toISOString().slice(0, 10);

export const JobFollowups = ({ jobId }) => {
  const { user } = useAuth();
  const canWrite = can(user, "reminders:write");
  const canRead = can(user, "reminders:read");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | snooze
  const [snoozeId, setSnoozeId] = useState(null);
  const [form, setForm] = useState({ date: todayStr(), time: "09:00", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get(`/jobs/${jobId}/followups`).then(({ data }) => setItems(data)).catch(() => {});

  useEffect(() => {
    if (canRead) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const openCreate = () => {
    setMode("create");
    setSnoozeId(null);
    setForm({ date: todayStr(), time: "09:00", note: "" });
    setOpen(true);
  };

  const openSnooze = (f) => {
    setMode("snooze");
    setSnoozeId(f.id);
    setForm({ date: f.date, time: f.time, note: f.note || "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.date || !form.time) return toast.error("Pick a date and time");
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post(`/jobs/${jobId}/followups`, form);
        toast.success("Reminder set — you'll get a notification when it's due");
      } else {
        await api.post(`/followups/${snoozeId}/snooze`, { date: form.date, time: form.time });
        toast.success("Reminder rescheduled");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const markDone = async (id) => {
    try {
      await api.post(`/followups/${id}/done`);
      toast.success("Reminder marked done");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!canRead) return null;

  const pending = items.filter((i) => i.status === "pending");
  const done = items.filter((i) => i.status === "done");
  const isOverdue = (f) => new Date(`${f.due_at}+05:30`) <= new Date();

  return (
    <Card className="border-brand-line shadow-none" data-testid="job-followups">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <BellPlus className="h-4 w-4 text-brand" strokeWidth={1.8} /> Follow-up reminders
        </CardTitle>
        {canWrite && (
          <Button size="sm" variant="outline" data-testid="set-reminder-btn" onClick={openCreate}>
            <BellPlus className="mr-1 h-4 w-4" /> Set reminder
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {pending.length === 0 && done.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="job-followups-empty">
            No reminders set for this job.
          </p>
        )}
        {pending.map((f) => (
          <div
            key={f.id}
            data-testid={`followup-${f.id}`}
            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${
              isOverdue(f) ? "border-crimson-200 bg-crimson-50/40" : "border-brand-line"
            }`}
          >
            <div>
              <p className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
                <Clock className="h-4 w-4 text-slate-400" />
                {f.date} at {f.time}
                {isOverdue(f) && (
                  <span className="rounded-full bg-crimson-500/10 px-2 py-0.5 text-xs font-medium text-crimson-600">
                    Due
                  </span>
                )}
              </p>
              {f.note && <p className="mt-1 text-sm text-slate-600">{f.note}</p>}
              <p className="mt-0.5 text-xs text-slate-400">Set by {f.created_by_name}</p>
            </div>
            {canWrite && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`snooze-followup-${f.id}`}
                  onClick={() => openSnooze(f)}
                >
                  <Clock className="mr-1 h-4 w-4" /> Snooze
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`done-followup-${f.id}`}
                  onClick={() => markDone(f.id)}
                >
                  <Check className="mr-1 h-4 w-4" /> Done
                </Button>
              </div>
            )}
          </div>
        ))}
        {done.map((f) => (
          <div
            key={f.id}
            data-testid={`followup-done-${f.id}`}
            className="flex items-center justify-between gap-3 rounded-lg border border-brand-line bg-slate-50 p-3 text-slate-400"
          >
            <p className="flex items-center gap-2 text-sm line-through">
              <Check className="h-4 w-4 text-emerald-500" />
              {f.date} at {f.time}
              {f.note ? ` — ${f.note}` : ""}
            </p>
            <span className="text-xs">Done</span>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="followup-dialog">
          <DialogHeader>
            <DialogTitle>{mode === "create" ? "Set a follow-up reminder" : "Snooze reminder"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  className="mt-1.5"
                  data-testid="followup-date-input"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <Input
                  type="time"
                  className="mt-1.5"
                  data-testid="followup-time-input"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
            </div>
            {mode === "create" && (
              <div>
                <Label className="text-xs">Note</Label>
                <Input
                  className="mt-1.5"
                  data-testid="followup-note-input"
                  placeholder="e.g. Call to confirm booking"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="save-followup-btn" disabled={saving} onClick={submit}>
              {mode === "create" ? "Set reminder" : "Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
